import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  UserPlus, Phone, Mail, ArrowRightCircle, Pencil, Trash2, MessageSquare, BellOff, ChevronDown,
} from 'lucide-react';
import { useFacility } from '../context/FacilityContext';
import leadsService from '../services/leadsService';
import apiClient from '../utils/apiClient';

const STATUS_STYLES = {
  new: 'bg-blue-50 text-blue-600',
  contacted: 'bg-amber-50 text-amber-600',
  converted: 'bg-emerald-50 text-emerald-600',
  lost: 'bg-red-50 text-red-500',
  // Set aside by an operator (spam, a vendor pitch). Kept as a row; stops
  // flashing on the dashboard because it is no longer an OPEN lead.
  dismissed: 'bg-slate-100 text-slate-500',
  dormant: 'bg-slate-50 text-slate-400',
  // Marked by the system at intake (a hidden field was filled, or two
  // machine signals agreed). Kept as a row; hidden from the default view.
  spam: 'bg-rose-50 text-rose-500',
};

// Set-aside rows bury the real lead between them, so the default view shows
// only what still needs a person. Nothing is deleted — each pile is one
// click away, with its count.
const HIDDEN_BY_DEFAULT = ['spam', 'dismissed'];
const VIEWS = [
  { key: 'active', label: 'Active' },
  { key: 'spam', label: 'Spam' },
  { key: 'dismissed', label: 'Dismissed' },
  { key: 'all', label: 'All' },
];
const inView = (lead, view) => {
  if (view === 'all') return true;
  if (view === 'active') return !HIDDEN_BY_DEFAULT.includes(lead.status);
  return lead.status === view;
};
const SPAM_REASON_TEXT = {
  honeypot: 'filled a field people never see',
  random_name: 'name looks machine-generated',
  dotted_gmail: 'address pattern used by form bots',
};
const spamReasonText = (lead) =>
  (lead.spam_reasons || []).map((r) => SPAM_REASON_TEXT[r] || r).join(' · ');

const Leads = () => {
  const { facilityId, loading: facilityLoading } = useFacility();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  // Non-null while editing an existing lead; the form submits a PUT
  // instead of a POST so Save doesn't create a duplicate row.
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', email: '', unit_interest: '', source: 'website' });
  const [view, setView] = useState('active');
  const visibleLeads = leads.filter((l) => inView(l, view));
  const viewCount = (key) => leads.filter((l) => inView(l, key)).length;

  const fetchLeads = useCallback(async () => {
    if (!facilityId) {
      if (!facilityLoading) setLoading(false);
      return;
    }
    setLoading(true);
    try {
      // Two pools, one inbox. `leads` are real lead rows. The prospect
      // endpoint also returns waitlist customers (interested, no rental) and
      // incomplete signups (abandoned wizard drafts) — both are leads in
      // every sense except which collection they happen to live in. They
      // only became lead ROWS after the 8-day dormant sweep, so for their
      // first week they existed solely on the dashboard banner and this page
      // said "No leads yet" while the banner insisted someone was waiting.
      //
      // allSettled: an unavailable prospect pool must not blank out the real
      // leads (a coupled Promise.all has hidden whole tabs here before).
      const [leadsRes, prospectsRes] = await Promise.allSettled([
        leadsService.getAll(facilityId),
        apiClient.get(`/api/facilities/${facilityId}/dashboard/pending-prospects?limit=100`),
      ]);
      const realLeads = leadsRes.status === 'fulfilled' ? leadsRes.value.data : [];
      const known = new Set(
        realLeads.flatMap((l) => [l.email, l.phone].filter(Boolean)
          .map((v) => String(v).toLowerCase())),
      );
      const extra = (prospectsRes.status === 'fulfilled'
        ? (prospectsRes.value.data.items || []) : []
      )
        // A prospect already represented by a lead row must not double-list.
        .filter((p) => p.source !== 'lead')
        .filter((p) => ![p.email, p.phone].filter(Boolean)
          .some((v) => known.has(String(v).toLowerCase())))
        .map((p) => ({
          _id: p.id,
          name: p.name,
          email: p.email,
          phone: p.phone,
          source: p.source === 'waitlist' ? 'waitlist' : 'incomplete signup',
          // Where the signup stopped — the fact an operator triages on.
          stall_stage: p.stall_stage || null,
          status: 'new',
          unit_interest: null,
          // Not a `leads` row, so the status dropdown and Convert/Edit/Delete
          // do not apply — it is handled on the customer record instead.
          _readOnly: true,
          _link: p.link,
        }));
      setLeads([...realLeads, ...extra]);
    } catch (err) {
      console.error('Failed to fetch leads:', err);
    } finally {
      setLoading(false);
    }
  }, [facilityId, facilityLoading]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      if (editingId) {
        await leadsService.update(facilityId, editingId, form);
      } else {
        await leadsService.create(facilityId, form);
      }
      setShowForm(false);
      setEditingId(null);
      setForm({ name: '', phone: '', email: '', unit_interest: '', source: 'website' });
      fetchLeads();
    } catch (err) {
      setError(err?.response?.data?.detail
        || `Could not ${editingId ? 'update' : 'create'} lead`);
    }
  };

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await leadsService.update(facilityId, leadId, { status: newStatus });
      fetchLeads();
    } catch (err) {
      console.error('Failed to update lead:', err);
    }
  };

  // Clicking a row opens it: the full message the person sent (a website
  // submission is mostly its message, and the table showed everything BUT
  // that), plus Dismiss. Dismiss keeps the row and takes it out of the open
  // pool, so it stops flashing on the dashboard without being deleted —
  // spam is worth keeping as a record, not worth a "lost".
  const [expandedId, setExpandedId] = useState(null);
  const toggleExpanded = (lead) =>
    setExpandedId((cur) => (cur === lead._id ? null : lead._id));
  const handleDismiss = async (lead) => {
    try {
      await leadsService.update(facilityId, lead._id, { status: 'dismissed' });
      setExpandedId(null);
      fetchLeads();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not dismiss lead');
    }
  };

  // Edit reuses the quick-add form: prefill it, then PUT instead of POST.
  const startEdit = (lead) => {
    setEditingId(lead._id);
    setForm({
      name: lead.name || '',
      phone: lead.phone || '',
      email: lead.email || '',
      unit_interest: lead.unit_interest || '',
      move_in_date: lead.move_in_date || '',
      source: lead.source || 'website',
      notes: lead.notes || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (lead) => {
    if (!window.confirm(
      `Delete ${lead.name || 'this lead'}? This removes them from the list.`
    )) return;
    try {
      await leadsService.remove(facilityId, lead._id);
      fetchLeads();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not delete lead');
    }
  };

  const handleConvert = async (leadId) => {
    try {
      await leadsService.convert(facilityId, leadId, {});
      fetchLeads();
    } catch (err) {
      console.error('Failed to convert lead:', err);
    }
  };

  if (loading || facilityLoading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!facilityId) return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <h2 className="text-lg font-semibold text-slate-900">No facility selected</h2>
      <p className="text-[13px] text-slate-500 mt-1">Create a facility in Settings to capture leads.</p>
    </div>
  );

  return (
    <div>
      {/* Sub-header strip (parent RentalsPage owns the H1 + the
          New Lead/New Rental buttons above the tabs). Just shows
          the count and the quick-add toggle. */}
      <div className="flex justify-between items-center mb-4 text-[13px] text-slate-500">
        <span>{leads.length} lead{leads.length === 1 ? '' : 's'}</span>
        {error && <span className="text-[12px] text-red-500 ml-3">{error}</span>}
        <button
          onClick={() => setShowForm(!showForm)}
          className="inline-flex items-center gap-1.5 text-slate-600 hover:text-blue-600 transition-colors"
        >
          <UserPlus className="h-3.5 w-3.5" />
          {showForm ? 'Hide quick-add' : 'Quick-add lead'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="rounded-xl border border-slate-200/80 bg-white p-5 mb-4 grid grid-cols-2 gap-3">
          <input placeholder="Name *" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-lg border border-slate-200 px-3 py-2 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" />
          <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-lg border border-slate-200 px-3 py-2 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" />
          <input placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-lg border border-slate-200 px-3 py-2 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" />
          <input placeholder="Unit Interest (e.g. 10x20)" value={form.unit_interest} onChange={(e) => setForm({ ...form, unit_interest: e.target.value })} className="rounded-lg border border-slate-200 px-3 py-2 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" />
          <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="rounded-lg border border-slate-200 px-3 py-2 text-[13px] text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500">
            <option value="website">Website</option>
            <option value="phone">Phone</option>
            <option value="walk_in">Walk-in</option>
            <option value="ai_voice_agent">AI Voice Agent</option>
            <option value="ai_web_chat">AI Web Chat</option>
            <option value="sparefoot">SpareFoot</option>
            <option value="uhaul">U-Haul</option>
            <option value="referral">Referral</option>
          </select>
          <button type="submit" className="inline-flex items-center justify-center px-3.5 py-2 rounded-lg bg-emerald-600 text-white text-[13px] font-medium hover:bg-emerald-700 transition-colors">Save Lead</button>
        </form>
      )}

      <div className="flex flex-wrap items-center gap-1.5 mb-3" role="tablist" aria-label="Lead views">
        {VIEWS.map((v) => (
          <button
            key={v.key}
            type="button"
            role="tab"
            aria-selected={view === v.key}
            onClick={() => { setView(v.key); setExpandedId(null); }}
            data-testid={`leads-view-${v.key}`}
            className={`px-3 py-1 rounded-full text-[12px] font-medium transition-colors ${
              view === v.key
                ? 'bg-blue-600 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {v.label} ({viewCount(v.key)})
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200/80 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
          <thead>
            <tr className="bg-slate-50/50 border-b border-slate-100">
              <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Name</th>
              <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Contact</th>
              <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Interest</th>
              <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Source</th>
              <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Status</th>
              <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {visibleLeads.map((lead) => (
              <React.Fragment key={lead._id}>
              <tr
                className={`hover:bg-slate-50/50 transition-colors cursor-pointer ${expandedId === lead._id ? 'bg-slate-50/70' : ''}`}
                onClick={() => toggleExpanded(lead)}
                data-testid={`lead-row-${lead._id}`}
              >
                <td className="px-4 py-3 text-[13px] font-medium text-slate-700">
                  <span className="inline-flex items-center gap-1.5">
                    <ChevronDown className={`h-3.5 w-3.5 text-slate-300 transition-transform ${expandedId === lead._id ? 'rotate-180' : ''}`} />
                    {lead.name}
                    {lead.notes && <MessageSquare className="h-3 w-3 text-slate-300" aria-label="has a message" />}
                    {lead.status !== 'spam' && (lead.spam_reasons || []).length > 0 && (
                      <span
                        className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700"
                        title={spamReasonText(lead)}
                        data-testid={`lead-suspect-${lead._id}`}
                      >
                        possible spam
                      </span>
                    )}
                  </span>
                </td>
                <td className="px-4 py-3 text-[13px] text-slate-500">
                  {lead.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3 text-slate-300" />{lead.phone}</span>}
                  {lead.email && <span className="flex items-center gap-1 mt-0.5"><Mail className="h-3 w-3 text-slate-300" />{lead.email}</span>}
                </td>
                <td className="px-4 py-3 text-[13px] text-slate-500">
                  {lead.unit_interest || '—'}
                  {lead.stall_stage && (
                    <span
                      className={`ml-2 text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                        lead.stall_stage === 'signed_unpaid'
                          ? 'bg-amber-50 text-amber-700'
                          : 'bg-slate-100 text-slate-500'
                      }`}
                      title={lead.stall_stage === 'signed_unpaid'
                        ? 'Signed the lease online but never paid'
                        : 'Started online and stopped before signing'}
                    >
                      {lead.stall_stage === 'signed_unpaid' ? 'signed, unpaid' : 'unsigned'}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-[13px] text-slate-500">{lead.source}</td>
                <td className="px-4 py-3">
                  {lead._readOnly ? (
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">
                      New
                    </span>
                  ) : (
                  <select
                    value={lead.status}
                    onChange={(e) => handleStatusChange(lead._id, e.target.value)}
                    className={`text-[10px] font-medium px-2 py-0.5 rounded-full border-0 cursor-pointer ${STATUS_STYLES[lead.status] || 'bg-slate-50 text-slate-500'}`}
                  >
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="converted">Converted</option>
                    <option value="lost">Lost</option>
                    <option value="dismissed">Dismissed</option>
                    <option value="spam">Spam</option>
                    <option value="dormant">Dormant</option>
                  </select>
                  )}
                </td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  {/* Convert / Edit / Delete. A lead used to be a one-way row:
                      the only action was Convert, so a typo'd phone number or
                      a duplicate could only be marked "lost" and then sat in
                      the list forever. */}
                  <div className="flex items-center gap-3">
                    {lead._readOnly ? (
                      <Link to={lead._link || '/customers'}
                        className="inline-flex items-center gap-1 text-[13px] text-blue-600 hover:text-blue-700 font-medium">
                        <ArrowRightCircle className="h-3.5 w-3.5" /> Open
                      </Link>
                    ) : (<>
                    {!['converted', 'lost', 'dismissed', 'spam'].includes(lead.status) && (
                      <button onClick={() => handleConvert(lead._id)} className="inline-flex items-center gap-1 text-[13px] text-emerald-600 hover:text-emerald-700 font-medium transition-colors">
                        <ArrowRightCircle className="h-3.5 w-3.5" /> Convert
                      </button>
                    )}
                    <button
                      onClick={() => startEdit(lead)}
                      className="inline-flex items-center gap-1 text-[13px] text-blue-600 hover:text-blue-700 font-medium transition-colors"
                    >
                      <Pencil className="h-3.5 w-3.5" /> Edit
                    </button>
                    <button
                      onClick={() => handleDelete(lead)}
                      className="inline-flex items-center gap-1 text-[13px] text-red-500 hover:text-red-600 font-medium transition-colors"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Delete
                    </button>
                    </>)}
                  </div>
                </td>
              </tr>
              {expandedId === lead._id && (
                <tr className="bg-slate-50/70" data-testid={`lead-detail-${lead._id}`}>
                  <td colSpan="6" className="px-4 pb-4 pt-1">
                    <div className="rounded-lg border border-slate-200 bg-white p-3">
                      <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1">
                        Message
                      </div>
                      {lead.notes ? (
                        <p className="text-[13px] text-slate-700 whitespace-pre-wrap">{lead.notes}</p>
                      ) : (
                        <p className="text-[13px] text-slate-400">No message was sent with this lead.</p>
                      )}
                      <div className="mt-3 flex items-center gap-4 text-[12px] text-slate-500">
                        {lead.created_at && (
                          <span>Received {new Date(lead.created_at + (lead.created_at.endsWith('Z') ? '' : 'Z')).toLocaleString()}</span>
                        )}
                        {lead.move_in_date && <span>Move-in {lead.move_in_date}</span>}
                        {lead.status === 'spam' && (
                          <>
                            <span className="text-slate-400">
                              Marked as spam automatically{spamReasonText(lead) ? ` — ${spamReasonText(lead)}` : ''}
                            </span>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); handleStatusChange(lead._id, 'new'); }}
                              className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[12px] font-medium"
                              data-testid={`lead-not-spam-${lead._id}`}
                            >
                              Not spam
                            </button>
                          </>
                        )}
                        {!lead._readOnly && !['dismissed', 'converted', 'spam'].includes(lead.status) && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleDismiss(lead); }}
                            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-600 hover:bg-slate-700 text-white text-[12px] font-medium"
                            data-testid={`lead-dismiss-${lead._id}`}
                          >
                            <BellOff className="h-3.5 w-3.5" /> Dismiss
                          </button>
                        )}
                        {lead.status === 'dismissed' && (
                          <span className="ml-auto text-slate-400">Dismissed — no longer flashing on the dashboard</span>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              )}
              </React.Fragment>
            ))}
            {visibleLeads.length === 0 && (
              <tr><td colSpan="6" className="px-4 py-8 text-center text-[13px] text-slate-400">
                {leads.length === 0 ? 'No leads yet' : 'Nothing in this view'}
              </td></tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
};

export default Leads;
